import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useCart } from '@/features/cart/context/CartContext';
import { apiClient } from '@/lib/apiClient';
import { getVendorSlugFromHostname, getBareDomainUrl } from '@/lib/subdomain';
import './navigation.css';

/* ------------------------------------------------------------------ *
 * Category tree helpers
 * ------------------------------------------------------------------ */
function buildCategoryTree(items) {
  const byId = new Map(items.map((category) => [category.id, category]));

  const roots = items.filter((category) => !category.parents?.length);

  const childrenOf = (category) =>
      (category.children ?? [])
          .map((child) => byId.get(child.id))
          .filter(Boolean);

  return { roots, childrenOf };
}

function CategoryMenuItem({ category, childrenOf, path, onNavigate }) {
  const [expanded, setExpanded] = useState(false);

  const kids = childrenOf(category).filter((child) => !path.includes(child.id));
  const nextPath = [...path, category.id];

  return (
      <div className="category-node">
        <div className="category-row">
          <Link
              to={`/products?category_id=${category.id}`}
              onClick={onNavigate}
          >
            {category.name_fa}
          </Link>

          {kids.length > 0 && (
              <button
                  type="button"
                  className="category-toggle"
                  aria-expanded={expanded}
                  aria-label={`زیرمجموعه‌های ${category.name_fa}`}
                  onClick={() => setExpanded((open) => !open)}
              >
                <i className={`fas fa-chevron-${expanded ? 'up' : 'down'}`} />
              </button>
          )}
        </div>

        {expanded && kids.length > 0 && (
            <div className="category-children">
              {kids.map((child) => (
                  <CategoryMenuItem
                      key={`${nextPath.join('-')}-${child.id}`}
                      category={child}
                      childrenOf={childrenOf}
                      path={nextPath}
                      onNavigate={onNavigate}
                  />
              ))}
            </div>
        )}
      </div>
  );
}

export default function Navigation() {
  const vendorSlug = getVendorSlugFromHostname(window.location.hostname);
  const homeHref = vendorSlug ? getBareDomainUrl() : '/';
  const {
    user,
    activeRole,
    logout,
    isSuperAdmin,
    isIrancharsouqAdmin,
    isInspector,
  } = useAuth();

  const { cart, clearCart } = useCart();

  const navigate = useNavigate();
  const location = useLocation();

  const headerRef = useRef(null);
  const mobileNavRef = useRef(null);
  const catalogMenuRef = useRef(null);

  const [isScrolled, setIsScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchInputRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [isCategoryMenuOpen, setIsCategoryMenuOpen] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isMobileCategoriesOpen, setIsMobileCategoriesOpen] = useState(false);

  const canBrowseCatalog = !user || activeRole === 'customer';
  const isCustomer = !user || activeRole === 'customer';
  const customerName = [user?.first_name, user?.last_name]
      .filter(Boolean)
      .join(' ');

  /* === [ADDED] تشخیص عنوان پنل و نقش برای نمایش در ناوبار === */
  const getPanelInfo = () => {
    const path = location.pathname;
    if (path.startsWith('/super-admin')) return { title: 'مدیریت کل سیستم', role: 'سوپر ادمین' };
    if (path.startsWith('/irancharsouq-admin')) return { title: 'مدیریت محصولات و سفارشات', role: 'ادمین ایران چارسوق' };
    if (path.startsWith('/company')) return { title: 'پنل شرکت', role: 'شرکت' };
    if (path.startsWith('/vendor')) return { title: 'مدیریت محصولات و سفارشات فروشگاه', role: 'فروشنده' };
    if (path.startsWith('/reports')) return { title: 'گزارش‌ها', role: 'مدیریت' };
    if (path.startsWith('/inspector')) return { title: 'پنل بازرسی', role: 'بازرس' };
    return null;
  };
  const panelInfo = getPanelInfo();
  /* ========================================================= */

  const { roots: rootCategories, childrenOf } = useMemo(
      () => buildCategoryTree(categories),
      [categories]
  );

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const headerEl = headerRef.current;
    if (!headerEl) return undefined;

    const updateNavHeight = () => {
      document.documentElement.style.setProperty(
          '--nav-height',
          `${headerEl.offsetHeight}px`
      );
    };

    updateNavHeight();

    const resizeObserver = new ResizeObserver(updateNavHeight);
    resizeObserver.observe(headerEl);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (!canBrowseCatalog) return undefined;

    const fetchCategories = async () => {
      const { data, error } = await apiClient.GET(
          '/api/v1/products/categories',
          { params: { query: { page: 1, size: 100 } } }
      );

      if (!error && data) {
        setCategories(data.items ?? []);
      }
    };

    fetchCategories().catch((error) => {
      console.error('Error fetching categories:', error);
    });

    return undefined;
  }, [canBrowseCatalog]);

  useEffect(() => {
    if (isSearchOpen) {
      searchInputRef.current?.focus();
    }
  }, [isSearchOpen]);

  useEffect(() => {
    setIsSearchOpen(false);
    setIsMobileNavOpen(false);
    setIsCategoryMenuOpen(false);
    setIsMobileCategoriesOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!isCategoryMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!catalogMenuRef.current?.contains(event.target)) {
        setIsCategoryMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsCategoryMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isCategoryMenuOpen]);

  useEffect(() => {
    if (!isMobileNavOpen) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      const clickedInsideHeader = headerRef.current?.contains(target);
      const clickedInsideMobileNav = mobileNavRef.current?.contains(target);

      if (!clickedInsideHeader && !clickedInsideMobileNav) {
        setIsMobileNavOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsMobileNavOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobileNavOpen]);

  useEffect(() => {
    if (isMobileNavOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileNavOpen]);

  const cartCount = cart?.reduce(
      (total, item) => total + (Number(item.quantity) || 0),
      0
  );

  const profilePath = isSuperAdmin
      ? '/super-admin/dashboard'
      : isIrancharsouqAdmin
          ? '/irancharsouq-admin/profile'
          : isInspector
              ? '/inspector'
              : activeRole === 'vendor'
                  ? '/vendor/profile'
                  : '/profile';

  const handleLogout = async () => {
    setIsMobileNavOpen(false);
    await logout();
    clearCart();
    localStorage.removeItem('basket_sim_package_id');
    localStorage.removeItem('basket_sim_quantities');
    localStorage.removeItem('basket_sim_result');
    navigate('/');
  };

  const handleSearch = (event) => {
    event.preventDefault();
    const query = searchQuery.trim();

    navigate(query ? `/products?q=${encodeURIComponent(query)}` : '/products');
  };

  const handleMobileNavBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      setIsMobileNavOpen(false);
    }
  };

  const closeCategoryMenu = () => setIsCategoryMenuOpen(false);
  const closeMobileNav = () => setIsMobileNavOpen(false);

  return (
      <header
          ref={headerRef}
          className={`header ${isScrolled ? 'scrolled' : ''}`}
      >
        <div className="container">
          <div className="header-inner">

            {/* بخش راست: لوگو + عنوان پنل */}
            <div className="header-brand-wrapper">
              {vendorSlug ? (
                  <a href={homeHref} className="header-logo">
                    <i className="fas fa-shopping-bag" />
                    <span>ایران چارسوق</span>
                  </a>
              ) : (
                  <Link to="/" className="header-logo">
                    <i className="fas fa-shopping-bag" />
                    <span>ایران چارسوق</span>
                  </Link>
              )}

              {panelInfo && user && (
                  <div className="header-panel-title">
                    {panelInfo.title}
                  </div>
              )}
            </div>

            <nav className="header-nav">
              {vendorSlug ? (
                  <a href={homeHref} className="header-nav-primary">خانه</a>
              ) : (
                  <Link to="/" className="header-nav-primary">خانه</Link>
              )}

              {canBrowseCatalog && (
                  <div className="header-catalog-menu" ref={catalogMenuRef}>
                    <button
                        type="button"
                        className="header-nav-primary header-catalog-button"
                        onClick={() => setIsCategoryMenuOpen((open) => !open)}
                        aria-expanded={isCategoryMenuOpen}
                        aria-haspopup="true"
                    >
                      محصولات
                      <i className="fas fa-chevron-down" />
                    </button>

                    {isCategoryMenuOpen && (
                        <div className="header-category-list">
                          <Link to="/products" onClick={closeCategoryMenu}>
                            همه محصولات
                          </Link>
                          {rootCategories.map((category) => (
                              <CategoryMenuItem
                                  key={category.id}
                                  category={category}
                                  childrenOf={childrenOf}
                                  path={[]}
                                  onNavigate={closeCategoryMenu}
                              />
                          ))}
                        </div>
                    )}
                  </div>
              )}

              {canBrowseCatalog && (
                  <Link to="/basket-simulator" className="header-nav-primary">
                    خرید گروهی کالاها
                  </Link>
              )}

              {canBrowseCatalog && (
                  <Link to="/marketplace" className="header-nav-primary">
                    فروشندگان
                  </Link>
              )}

              {isSuperAdmin && (
                  <Link to="/super-admin/dashboard">
                    پنل سوپر ادمین
                  </Link>
              )}

              {isIrancharsouqAdmin && (
                  <Link to="/irancharsouq-admin">
                    پنل مدیریت
                  </Link>
              )}

              {isInspector && (
                  <Link to="/inspector">
                    پنل بازرسی
                  </Link>
              )}

              {activeRole === 'vendor' && (
                  <Link to="/vendor/dashboard">
                    پنل فروشنده
                  </Link>
              )}

              {activeRole === 'company' && (
                  <Link to="/company/dashboard">
                    پنل شرکت
                  </Link>
              )}

              <Link to="/about" className="header-nav-primary">
                درباره ما
              </Link>

              <Link to="/contact" className="header-nav-primary">
                تماس با ما
              </Link>
            </nav>

            <div className="header-utilities">
              <form
                  className={`header-search ${isSearchOpen ? 'open' : ''}`}
                  onSubmit={handleSearch}
                  onClick={() => setIsSearchOpen(true)}
              >
                <input
                    ref={searchInputRef}
                    type="search"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="جستجوی محصولات و فروشندگان"
                    aria-label="جستجوی محصولات و فروشندگان"
                />
                <button
                    type={isSearchOpen ? 'submit' : 'button'}
                    title="جستجو"
                    aria-label="جستجو"
                    onClick={() => setIsSearchOpen(true)}
                >
                  <i className="fas fa-search" />
                </button>
                {isSearchOpen && (
                    <button
                        type="button"
                        className="mobile-search-close"
                        aria-label="بستن جستجو"
                        onClick={(event) => {
                          event.stopPropagation();
                          setIsSearchOpen(false);
                        }}
                    >
                      <i className="fas fa-times" />
                    </button>
                )}
              </form>

              <button
                  type="button"
                  className="mobile-search-trigger"
                  aria-label="جستجو"
                  onClick={() => setIsSearchOpen(true)}
              >
                <i className="fas fa-search" />
              </button>

              {/* بخش چپ: دکمه‌ها و اطلاعات کاربر */}
              <div className="header-actions">
                {user ? (
                    <>
                      {panelInfo ? (
                          /* نمایش نام کنار دکمه خروج برای پنل‌ها (نقش حذف شد) */
                          <div className="header-user-inline-info">
                            <span className="user-name">{customerName || 'کاربر'}</span>
                          </div>
                      ) : (
                          /* نمایش لینک پروفایل برای مشتریان عادی */
                          !isSuperAdmin && !isIrancharsouqAdmin && !isInspector && activeRole !== 'vendor' && activeRole !== 'company' && (
                              <Link
                                  to={profilePath}
                                  className="header-profile-link"
                                  title="پروفایل"
                              >
                                <i className="fas fa-user" />
                                <span>{customerName || 'پروفایل'}</span>
                              </Link>
                          )
                      )}

                      {/* دکمه کیف پول فقط برای مشتریان عادی */}
                      {isCustomer && !panelInfo && (
                          <Link
                              to="/profile/wallet"
                              className="header-wallet-link"
                              title="کیف پول"
                              aria-label="کیف پول"
                          >
                            <i className="fas fa-wallet" />
                          </Link>
                      )}

                      <button
                          type="button"
                          onClick={handleLogout}
                          title="خروج"
                      >
                        <i className="fas fa-sign-out-alt" />
                      </button>
                    </>
                ) : (
                    <Link
                        to="/login"
                        state={{ from: location }}
                        className="btn btn-primary"
                        style={{
                          padding: '10px 20px',
                          fontSize: '0.85rem',
                        }}
                    >
                      ورود
                    </Link>
                )}

                {!isSuperAdmin && !isIrancharsouqAdmin && !isInspector && activeRole !== 'vendor' && activeRole !== 'company' && (
                    <Link
                        to="/cart"
                        className="cart-btn"
                        title="سبد خرید"
                    >
                      <i className="fas fa-shopping-cart" />

                      {cartCount > 0 && (
                          <span className="cart-badge">
                      {cartCount}
                    </span>
                      )}
                    </Link>
                )}
              </div>

              <button
                  type="button"
                  className="hamburger"
                  onClick={() => setIsMobileNavOpen((open) => !open)}
                  aria-expanded={isMobileNavOpen}
                  aria-label={isMobileNavOpen ? 'بستن منو' : 'باز کردن منو'}
              >
                <span />
                <span />
                <span />
              </button>
            </div>
          </div>
        </div>

        <div
            ref={mobileNavRef}
            className={`mobile-nav ${isMobileNavOpen ? 'open' : ''}`}
            onClick={handleMobileNavBackdropClick}
        >
          {user ? (
              <div className="mobile-account-section">
                <Link to={profilePath} className="mobile-nav-link" onClick={closeMobileNav}>
                  <i className="fas fa-user" />
                  <span>{customerName || 'پروفایل'}</span>
                </Link>
                <button type="button" className="mobile-nav-link mobile-logout" onClick={handleLogout}>
                  خروج
                </button>
              </div>
          ) : (
              <Link to="/login" state={{ from: location }} className="mobile-nav-link" onClick={closeMobileNav}>
                ورود
              </Link>
          )}

          {vendorSlug ? (
              <a href={homeHref} className="mobile-nav-link" onClick={closeMobileNav}>خانه</a>
          ) : (
              <Link to="/" className="mobile-nav-link" onClick={closeMobileNav}>خانه</Link>
          )}

          <div className="mobile-products">
            <div className="mobile-products-row">
              <Link to="/products" className="mobile-nav-link" onClick={closeMobileNav}>
                محصولات
              </Link>
              {canBrowseCatalog && rootCategories.length > 0 && (
                  <button
                      type="button"
                      className="category-toggle mobile-products-toggle"
                      aria-expanded={isMobileCategoriesOpen}
                      aria-label="دسته‌بندی‌ها"
                      onClick={() => setIsMobileCategoriesOpen((open) => !open)}
                  >
                    <i className={`fas fa-chevron-${isMobileCategoriesOpen ? 'up' : 'down'}`} />
                  </button>
              )}
            </div>

            {canBrowseCatalog && isMobileCategoriesOpen && (
                <div className="mobile-category-tree">
                  {rootCategories.map((category) => (
                      <CategoryMenuItem
                          key={category.id}
                          category={category}
                          childrenOf={childrenOf}
                          path={[]}
                          onNavigate={closeMobileNav}
                      />
                  ))}
                </div>
            )}
          </div>

          {canBrowseCatalog && (
              <Link to="/basket-simulator" className="mobile-nav-link" onClick={closeMobileNav}>
                خرید گروهی کالاها
              </Link>
          )}

          {canBrowseCatalog && (
              <Link to="/marketplace" className="mobile-nav-link" onClick={closeMobileNav}>فروشندگان</Link>
          )}

          {isCustomer && (
              <Link
                  to="/profile/wallet"
                  className="mobile-nav-link"
                  onClick={closeMobileNav}
              >
                کیف پول
              </Link>
          )}

          {isSuperAdmin && (
              <Link to="/super-admin/dashboard" className="mobile-nav-link" onClick={closeMobileNav}>
                پنل سوپر ادمین
              </Link>
          )}

          {isIrancharsouqAdmin && (
              <Link to="/irancharsouq-admin" className="mobile-nav-link" onClick={closeMobileNav}>
                پنل مدیریت
              </Link>
          )}

          {isInspector && (
              <Link to="/inspector" className="mobile-nav-link" onClick={closeMobileNav}>
                پنل بازرسی
              </Link>
          )}

          {activeRole === 'vendor' && (
              <Link to="/vendor/dashboard" className="mobile-nav-link" onClick={closeMobileNav}>
                پنل فروشنده
              </Link>
          )}

          {activeRole === 'company' && (
              <Link to="/company/dashboard" className="mobile-nav-link" onClick={closeMobileNav}>
                پنل شرکت
              </Link>
          )}

          <Link to="/about" className="mobile-nav-link" onClick={closeMobileNav}>درباره ما</Link>
          <Link to="/contact" className="mobile-nav-link" onClick={closeMobileNav}>تماس با ما</Link>
        </div>
      </header>
  );
}